import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import SharedHeaderContractor from './SharedHeaderContractor';
// CB:BEGIN contractor-inline-help-import
import ContractorInlineHelpShortcut from './help/ContractorInlineHelpShortcut';
// CB:END contractor-inline-help-import
import { useAuthFetch } from '../lib/useAuthFetch';
// CB:BEGIN demo-bids-shared-import
import { getDemoProjects, setSelectedDemoProject, getSelectedDemoProject } from '../lib/demoBids';
// CB:END demo-bids-shared-import

interface CurrentBidsProps {
  onNavigate: (screen: string) => void;
}

interface Project {
  id: number;
  title: string;
  location: string;
  details: string;
  budget: string;
  bids: number;
  expires: string;
  datePosted: string;
  sqFt: number;
  material: string;
}

const CurrentBids: React.FC<CurrentBidsProps> = ({ onNavigate }) => {
  const { user } = useUser();
  const authFetch = useAuthFetch();
  const [sortOrder, setSortOrder] = useState<'oldest' | 'newest' | 'sqft-largest' | 'sqft-smallest'>('oldest');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  // CB:BEGIN fetch-real-projects
  const [availableProjects, setAvailableProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // CB:BEGIN remove-bid-state
  const [removedProjectIds, setRemovedProjectIds] = useState<Set<string>>(new Set());
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<{show: boolean, projectId: string | null}>({ show: false, projectId: null });
  
  // Load removed project IDs from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('contractor_removed_project_ids');
      if (stored) {
        const ids = JSON.parse(stored);
        setRemovedProjectIds(new Set(ids));
      }
    } catch {
      // Ignore errors
    }
  }, []);
  
  // Save removed project IDs to localStorage
  const removeProjectFromDashboard = (projectId: string) => {
    const newRemoved = new Set(removedProjectIds);
    newRemoved.add(projectId);
    setRemovedProjectIds(newRemoved);
    try {
      localStorage.setItem('contractor_removed_project_ids', JSON.stringify(Array.from(newRemoved)));
      // CB:BEGIN notify-dashboard-of-removal
      // Dispatch custom event to notify dashboard of removal
      window.dispatchEvent(new CustomEvent('cb:projects-removed', { detail: { projectId } }));
      // CB:END notify-dashboard-of-removal
    } catch {
      // Ignore errors
    }
    setShowRemoveConfirm({ show: false, projectId: null });
  };
  // CB:END remove-bid-state
  
  // CB:BEGIN current-bids-refetch-callback
  const fetchProjects = React.useCallback(async () => {
    try {
      setLoading(true);
      const { getApiUrl } = await import('../lib/getApiUrl');
      const response = await authFetch(getApiUrl('/api/projects?status=BIDDING'));
      if (response.ok) {
        const projects = await response.json();
        const list = Array.isArray(projects) ? projects : (projects?.projects ?? []);
        if (list.length > 0) {
          console.log('✅ Fetched available projects:', list.length);
        }
        setAvailableProjects(list);
      } else {
        setAvailableProjects([]);
      }
    } catch (error) {
      console.error('❌ Error fetching projects:', error);
      setAvailableProjects([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);
  // CB:END current-bids-refetch-callback

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Refetch when a bid is submitted so count and "Submitted Bid" stay in sync
  useEffect(() => {
    const onRefresh = () => fetchProjects();
    window.addEventListener('cb:refetch-current-bids', onRefresh);
    window.addEventListener('cb:bid-submitted', onRefresh);
    return () => {
      window.removeEventListener('cb:refetch-current-bids', onRefresh);
      window.removeEventListener('cb:bid-submitted', onRefresh);
    };
  }, [fetchProjects]);

  // CB:BEGIN contractor-id-state-current-bids
  // Fetch the logged-in contractor's ID to accurately detect their own bids
  const [contractorId, setContractorId] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const userId = user?.id;
        if (!userId) return;
        const { getApiUrl } = await import('../lib/getApiUrl');
        const response = await fetch(getApiUrl(`/api/contractors/self?userId=${userId}`));
        if (response.ok) {
          const data = await response.json();
          if (data?.contractor?.id) setContractorId(data.contractor.id);
        }
      } catch {}
    })();
  }, [user?.id]);
  // CB:END contractor-id-state-current-bids

  // CB:BEGIN contractor-demo-reset
  // Fresh demo state on first contractor visit each session
  useEffect(() => {
    try {
      const interacted = sessionStorage.getItem('contractor_interacted') === '1';
      if (!interacted) {
        localStorage.removeItem('contractor_demo_state');
        localStorage.removeItem('open_selected_demo_project');
        // Do not remove active_project_id here; homeowner side may use it
      }
    } catch {}
  }, []);
  // CB:END contractor-demo-reset

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // CB:BEGIN auto-open-selected-project
  useEffect(() => {
    try {
      const flag = localStorage.getItem('open_selected_demo_project');
      const selected = getSelectedDemoProject();
      if (flag === '1' && selected) {
        localStorage.removeItem('open_selected_demo_project');
        onNavigate(`/contractor/projects/${selected.id}`);
      }
    } catch {}
  }, []);
  // CB:END auto-open-selected-project
  
  // FIX: Support query param to auto-open a given project from the list
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const openId = urlParams.get("open");
      if (openId) {
        onNavigate(`/contractor/projects/${openId}`);
      }
    } catch {}
  }, [onNavigate]);
  // Convert fetched projects to the format expected by the UI
  const projects: (Project & { projectData?: any, status?: string })[] = 
    availableProjects.length > 0 
      ? availableProjects.map((p, idx) => ({
          id: p.id || `project-${idx}`, // Use actual project ID from database
          title: p.address || 'Address TBD',
          location: `${p.projectType || 'Residential'} | ${p.status || 'Open'}`,
          details: `Bids: ${p.bids?.length || 0} | ${p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'Posted recently'}`,
          budget: 'TBD',
          bids: p.bids?.length || 0,
          expires: 'Open',
          datePosted: p.createdAt ? new Date(p.createdAt).toISOString().slice(0,10) : new Date().toISOString().slice(0,10),
          sqFt: 0,
          material: p.projectType || 'Asphalt Shingles',
          status: p.status || 'REQUESTED',
          projectData: p // Store full project data for navigation
        }))
      : [];

  // CB:BEGIN filter-projects-by-availability
  // Don't filter out projects if showing real projects from API
  let hiddenIds: string[] = [];
  try { hiddenIds = JSON.parse(localStorage.getItem('contractor_bidded_ids') || '[]') } catch {}
  const activeId = localStorage.getItem('active_project_id') || '';
  // Filter out removed projects
  const filtered = availableProjects.length > 0 
    ? projects.filter(p => 
        p.status !== 'CLOSED' && 
        p.status !== 'COMPLETED' && 
        p.status !== 'BID_ACCEPTED' &&
        !removedProjectIds.has(String(p.id)) // CB:BEGIN filter-removed-projects - Hide removed projects
      )
    : projects.filter(p => 
        !hiddenIds.includes(String(p.id)) && 
        String(p.id) !== activeId &&
        !removedProjectIds.has(String(p.id)) // CB:BEGIN filter-removed-projects - Hide removed projects
      );
  // CB:END filter-projects-by-availability

  const sortedProjects = [...filtered].sort((a, b) => {
    if (sortOrder === 'oldest') {
      return new Date(a.datePosted).getTime() - new Date(b.datePosted).getTime();
    } else if (sortOrder === 'newest') {
      return new Date(b.datePosted).getTime() - new Date(a.datePosted).getTime();
    } else if (sortOrder === 'sqft-largest') {
      return b.sqFt - a.sqFt;
    } else if (sortOrder === 'sqft-smallest') {
      return a.sqFt - b.sqFt;
    }
    return 0;
  });
  return (
    <div className="container">
      <SharedHeaderContractor onNavigate={onNavigate} />

      {/* Content */}
      <div className="content" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* CB:BEGIN contractor-inline-help-current-bids */}
        <ContractorInlineHelpShortcut
          onNavigate={onNavigate}
          sectionId="finding-projects"
          label="Need help finding projects or improving your bid?"
        />
        {/* CB:END contractor-inline-help-current-bids */}
        {/* Filter Section */}
        <div 
          ref={filterRef}
          style={{ 
            background: '#f9fafb', 
            padding: '16px 20px', 
            borderRadius: '8px', 
            marginTop: '32px',
            marginBottom: '24px',
            border: '1px solid #e5e7eb',
            position: 'relative'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* CB:BEGIN current-bids-heading-mobile */}
            <h2 style={{ fontSize: 'clamp(18px, 4.5vw, 28px)', margin: 0 }}>Open Projects for Bidding</h2>
            {/* CB:END current-bids-heading-mobile */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontWeight: 600, color: '#374151' }}>Filter</span>
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path 
                    d="M1 4h14M3 8h10M5 12h6" 
                    stroke="#374151" 
                    strokeWidth="2" 
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Dropdown Menu */}
          {showFilterMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '20px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              minWidth: '200px',
              marginTop: '8px'
            }}>
              <div style={{ padding: '8px 0' }}>
                <button
                  onClick={() => {
                    setSortOrder('oldest');
                    setShowFilterMenu(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: sortOrder === 'oldest' ? '#f3f4f6' : 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151',
                    borderBottom: '1px solid #f3f4f6',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (sortOrder !== 'oldest') {
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (sortOrder !== 'oldest') {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  Oldest First
                </button>
                <button
                  onClick={() => {
                    setSortOrder('newest');
                    setShowFilterMenu(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: sortOrder === 'newest' ? '#f3f4f6' : 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151',
                    borderBottom: '1px solid #f3f4f6',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (sortOrder !== 'newest') {
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (sortOrder !== 'newest') {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  Newest First
                </button>
                {/* Removed SQFT sort options per request */}
              </div>
            </div>
          )}
        </div>
        
        {/* Vertical Scrolling Projects List */}
        <div style={{ 
          maxHeight: 'calc(100svh - 300px)', 
          overflowY: 'auto',
          paddingRight: '8px'
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              Loading available projects...
            </div>
          ) : sortedProjects.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <h3 style={{ marginBottom: '8px' }}>No projects available</h3>
              <p style={{ fontSize: '14px' }}>Check back later for new bidding opportunities</p>
            </div>
          ) : (
            sortedProjects.map((project) => {
              // CB:BEGIN check-if-bidded-current-bids
            // Only mark as submitted if THIS contractor has a bid on this project
              let biddedIds: string[] = [];
              try { 
                biddedIds = JSON.parse(localStorage.getItem('contractor_bidded_ids') || '[]'); 
              } catch {}
            const localStorageBidded = Array.isArray(biddedIds) && biddedIds.includes(String(project.id));
            const currentEmail = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress;
            const bidsArray = Array.isArray((project as any).projectData?.bids) ? (project as any).projectData.bids : (Array.isArray((project as any).bids) ? (project as any).bids : []);
            const ownBidExists = bidsArray.some((bid: any) => {
              if (contractorId && bid.contractorId) return bid.contractorId === contractorId;
              if (currentEmail) return bid?.contractor?.user?.email === currentEmail;
              return false;
            });
            // Trust localStorage so "Submitted Bid" shows immediately after submit (before refetch)
            const hasBidded = ownBidExists || (localStorageBidded && !!contractorId);
            // CB:BEGIN closed-status-check
            const isClosed = (project as any).status === 'CLOSED' || project.bids === 3;
            // CB:END closed-status-check
              // CB:END check-if-bidded-current-bids
              
              // CB:BEGIN check-roof-report-current-bids
              // List API returns roofReportUploadedAt, roofReportUrl, roofReportRedactedKey. Use any to enable bidding.
              const projectData = (project as any).projectData || project;
              const hasRoofReport = !!(projectData.roofReportUrl ?? projectData.roofReportRedactedKey ?? projectData.roofReportUploadedAt);
              const isMissingReport = !hasRoofReport;
              // CB:END check-roof-report-current-bids
              
              return (
              <div 
                key={project.id}
                className="dashboard-card" 
                style={{ 
                  marginBottom: '16px',
                  padding: '20px',
                  border: isClosed 
                    ? '1px solid #dc2626' 
                    : isMissingReport
                      ? '2px solid #fca5a5' // Subdued red border when missing report
                      : hasBidded 
                        ? '2px solid #f97316' 
                        : '1px solid #e5e7eb', // CB:BEGIN orange-highlight-bidded
                  borderRadius: '8px',
                  background: isClosed 
                    ? '#fef2f2' 
                    : isMissingReport
                      ? '#fef2f2' // Subdued red background when missing report
                      : hasBidded 
                        ? '#fff7ed' 
                        : 'white', // Light orange background if bidded
                  boxShadow: isClosed 
                    ? '0 1px 3px rgba(220, 38, 38, 0.1)' 
                    : isMissingReport
                      ? '0 2px 8px rgba(239, 68, 68, 0.15)' // Subdued red shadow
                      : hasBidded 
                        ? '0 2px 8px rgba(249, 115, 22, 0.2)' 
                        : '0 1px 3px rgba(0, 0, 0, 0.1)',
                  cursor: (isClosed || isMissingReport) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  color: '#2563eb',
                  textDecoration: 'none',
                  position: 'relative',
                  opacity: isMissingReport ? 0.8 : 1
                }}
              onClick={() => {
                try { sessionStorage.setItem('contractor_interacted', '1'); } catch {}
                if (isClosed || isMissingReport) return;
                // CB:BEGIN removed-demo-fallback
                // Store the real project ID
                try { localStorage.setItem('active_project_id', String(project.id)); } catch {}
                // CB:END removed-demo-fallback
                // Navigate to project details
                onNavigate(`/contractor/projects/${project.id}`);
              }}
              onMouseEnter={(e) => {
                if (!isClosed && !isMissingReport) {
                  e.currentTarget.style.borderColor = hasBidded ? '#f97316' : '#2563eb';
                  e.currentTarget.style.boxShadow = hasBidded 
                    ? '0 4px 12px rgba(249, 115, 22, 0.25)' 
                    : '0 4px 12px rgba(37, 99, 235, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isClosed && !isMissingReport) {
                  e.currentTarget.style.borderColor = hasBidded ? '#f97316' : '#e5e7eb';
                  e.currentTarget.style.boxShadow = hasBidded 
                    ? '0 2px 8px rgba(249, 115, 22, 0.2)' 
                    : '0 1px 3px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                } else if (isMissingReport) {
                  // Reset to subdued red styling when missing report
                  e.currentTarget.style.borderColor = '#fca5a5';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {isClosed && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: '#dc2626',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '600',
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
                }}>
                  CLOSED
                </div>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                opacity: (isClosed || isMissingReport) ? 0.5 : 1
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: isMissingReport ? '#dc2626' : '#2563eb' }}>
                    {project.title}
                  </h3>
                  <p style={{ color: '#6b7280', marginBottom: '4px' }}>{project.location}</p>
                  {(project as any).projectData?.computedDistanceMiles != null && (
                    <p style={{ color: '#2563eb', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                      📍 {Math.max(0.1, (project as any).projectData.computedDistanceMiles).toFixed(1)} miles from you
                    </p>
                  )}
                  
                  {/* CB:BEGIN roof-report-indicator-current-bids */}
                  {isMissingReport && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: '12px',
                      width: '100%'
                    }}>
                      <span style={{ color: '#dc2626', fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>
                        ⚠️ Missing Roof Report - Bidding Disabled Until Admin Adds Report
                      </span>
                    </div>
                  )}
                  {/* CB:END roof-report-indicator-current-bids */}
                </div>
                <div style={{ textAlign: 'right', marginLeft: '16px' }}>
                  <div style={{ fontSize: '14px', color: '#374151', fontWeight: '500', marginBottom: '4px' }}>
                    {project.material}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {project.details}
                  </div>
                  {/* CB:BEGIN current-bids-submit-button */}
                  {!isClosed && (
                    <div style={{ marginTop: 8, display: 'flex', gap: '8px', flexDirection: 'column' }}>
                      {/* CB:BEGIN view-report-button-next-to-submit */}
                      {hasRoofReport && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const { getApiUrl } = await import('../lib/getApiUrl');
                              const projectId = (project as any).projectData?.id || project.id;
                              const apiUrl = getApiUrl(`/api/projects/${projectId}/roof-report/download?format=json`);
                              const response = await authFetch(apiUrl);
                              if (!response.ok) { alert('Failed to load roof report'); return; }
                              const data = await response.json();
                              if (data.downloadUrl) { window.open(data.downloadUrl, '_blank', 'noopener,noreferrer'); }
                              else { alert('Report not available'); }
                            } catch (error) {
                              console.error('Error loading roof report:', error);
                              alert('Failed to load roof report');
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            width: '100%'
                          }}
                        >
                          📄 View Report
                        </button>
                      )}
                      {/* CB:END view-report-button-next-to-submit */}
                      <button
                        className={hasBidded ? '' : 'btn btn-primary'}
                        disabled={hasBidded || isMissingReport}
                        onClick={(e) => {
                          if (hasBidded || isMissingReport) {
                            e.stopPropagation();
                            return;
                          }
                          e.stopPropagation();
                          try { sessionStorage.setItem('contractor_interacted', '1'); } catch {}
                          // CB:BEGIN active-project-id-real
                          // Mark project as active for real or demo projects
                          // FIX: Do NOT add to contractor_bidded_ids here - only add after successful bid submission
                          try { localStorage.setItem('active_project_id', String(project.id)); } catch {}
                          // CB:END active-project-id-real
                          // Open the specific project details page to submit bid
                          onNavigate(`/contractor/projects/${project.id}`);
                        }}
                        aria-label={hasBidded ? `Bid already submitted for ${project.title}` : isMissingReport ? `Bidding disabled - missing roof report` : `Open bid for ${project.title}`}
                        style={hasBidded ? {
                          backgroundColor: '#f97316',
                          color: 'white',
                          border: '1px solid #f97316',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'not-allowed',
                          opacity: 0.8,
                          width: '100%'
                        } : isMissingReport ? {
                          backgroundColor: '#f3f4f6',
                          color: '#9ca3af',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'not-allowed',
                          opacity: 0.6,
                          width: '100%'
                        } : {}}
                      >
                        {hasBidded ? 'Submitted Bid' : isMissingReport ? 'Submit Bid (Disabled)' : 'Submit Bid'}
                      </button>
                    </div>
                  )}
                  {/* CB:END current-bids-submit-button */}
                </div>
              </div>
              {/* CB:BEGIN remove-bid-button */}
              <div style={{ 
                position: 'absolute', 
                bottom: '16px', 
                left: '20px' 
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRemoveConfirm({ show: true, projectId: String(project.id) });
                  }}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: '1px solid #dc2626',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#b91c1c';
                    e.currentTarget.style.borderColor = '#b91c1c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                    e.currentTarget.style.borderColor = '#dc2626';
                  }}
                >
                  Remove Bid
                </button>
              </div>
              {/* CB:END remove-bid-button */}
            </div>
            );
            })
          )}
        </div>
      </div>
      
      {/* CB:BEGIN remove-bid-confirmation-modal */}
      {showRemoveConfirm.show && (
        <div 
          role="dialog" 
          aria-modal="true" 
          style={{ 
            position: 'fixed', 
            inset: 0, 
            background: 'rgba(0, 0, 0, 0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 9999 
          }}
          onClick={() => setShowRemoveConfirm({ show: false, projectId: null })}
        >
          <div 
            className="card" 
            style={{ 
              background: 'white', 
              borderRadius: '12px', 
              padding: '24px', 
              width: 'min(420px, 92vw)', 
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
              border: '2px solid #dc2626'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: '16px', 
              color: '#dc2626',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              Remove Bid?
            </h3>
            <p style={{ 
              color: '#374151', 
              marginBottom: '24px',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Are you sure you want to remove this bid from your dashboard? You will not have an opportunity to submit a bid again.
            </p>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px', 
              marginTop: '16px' 
            }}>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowRemoveConfirm({ show: false, projectId: null })}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button 
                className="btn"
                onClick={() => {
                  if (showRemoveConfirm.projectId) {
                    removeProjectFromDashboard(showRemoveConfirm.projectId);
                  }
                }}
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: '1px solid #dc2626',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
      {/* CB:END remove-bid-confirmation-modal */}
    </div>
  );
};

export default CurrentBids; 